import { useState } from "react";
import { ChevronRight, Users, TrendingUp, MessageSquare, Quote } from "lucide-react";
import RoasterAvatar from "./RoasterAvatar";
import { catMeta } from "../lib/verdictly";

function initialsOf(name) {
  return (name || "R").replace("@", "").slice(0, 2).toUpperCase();
}

// Progressive reveal: headline stat -> biggest concern -> individual responses.
export default function VerdictResult({ task, result, before, testPrefix = "result" }) {
  const [step, setStep] = useState(0);
  const meta = catMeta(task.category);
  const accent = meta.accent;
  if (!result) return null;

  const questions = task.questions || [];
  const qById = Object.fromEntries(questions.map((q) => [q.id, q]));
  const headline = result.headline || {};
  const concernField = result.concern_field;
  const commentField = result.comment_field;
  const positiveField = result.positive_field;
  const concernTexts = concernField ? (result.texts?.[concernField] || []) : [];
  const commentTexts = commentField ? (result.texts?.[commentField] || []) : [];
  const positiveTexts = positiveField ? (result.texts?.[positiveField] || []) : [];

  const themeTitle = task.category === "roast" ? "The harshest takes" : "The biggest concern";
  const themeTexts = concernTexts.length ? concernTexts : commentTexts;

  return (
    <div data-testid={`${testPrefix}-reveal`} className="space-y-5">
      {/* STEP 0 — headline stat */}
      <div className="rounded-xl border p-8 text-center relative overflow-hidden"
        style={{ borderColor: accent + "55", background: meta.soft }}>
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-400 flex items-center justify-center gap-2">
          <Users size={14} style={{ color: accent }} /> {result.reviews_count} strangers {headline.verb}
        </div>
        {before && before.display ? (
          <div data-testid={`${testPrefix}-beforeafter`} className="mt-3 flex items-center justify-center gap-3 font-heading font-bold">
            <span className="text-4xl sm:text-5xl text-zinc-500 line-through decoration-2">{before.display}</span>
            <ChevronRight size={30} style={{ color: accent }} />
            <span className="text-6xl sm:text-7xl" style={{ color: accent }}>{headline.display}</span>
          </div>
        ) : (
          <div data-testid={`${testPrefix}-headline`} className="mt-2 font-heading font-bold text-7xl sm:text-8xl" style={{ color: accent }}>
            {headline.display}
          </div>
        )}
        <div className="mt-1 text-sm font-bold tracking-widest uppercase text-zinc-300">{headline.label}</div>
      </div>

      {/* Scale breakdowns as quick bars */}
      <div className="grid sm:grid-cols-2 gap-3">
        {questions.filter((q) => q.type === "scale").map((q) => {
          const b = result.breakdown?.[q.id] || {};
          const pct = Math.round(((b.avg || 0) / (q.max || 10)) * 100);
          return (
            <div key={q.id} data-testid={`${testPrefix}-scale-${q.id}`} className="rounded-lg border border-white/10 bg-zinc-900/50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">{q.label}</span>
                <span className="font-heading font-bold text-lg" style={{ color: accent }}>{b.avg}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
              </div>
            </div>
          );
        })}
        {questions.filter((q) => q.type === "choice").map((q) => {
          const b = result.breakdown?.[q.id] || {};
          const counts = b.counts || {};
          return (
            <div key={q.id} data-testid={`${testPrefix}-choice-${q.id}`} className="rounded-lg border border-white/10 bg-zinc-900/50 p-4">
              <div className="text-sm text-zinc-400 mb-2">{q.label}</div>
              <div className="flex gap-2 flex-wrap">
                {q.options.map((opt) => (
                  <span key={opt} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-zinc-300">
                    {q.option_labels?.[opt] || opt}: <b style={{ color: accent }}>{counts[opt] || 0}</b>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {step === 0 && (
        <button data-testid={`${testPrefix}-reveal-concern`} onClick={() => setStep(1)}
          className="w-full py-3 rounded-full font-bold text-black transition-transform hover:-translate-y-0.5"
          style={{ background: accent }}>
          {task.category === "roast" ? "Show me the burns" : "Reveal the biggest concern"}
        </button>
      )}

      {/* STEP 1 — biggest concern / theme */}
      {step >= 1 && themeTexts.length > 0 && (
        <div data-testid={`${testPrefix}-concern`} className="rounded-xl border border-white/10 bg-zinc-900/50 p-6 animate-fade-up">
          <div className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase text-zinc-300 mb-4">
            <TrendingUp size={15} style={{ color: accent }} /> {themeTitle}
          </div>
          <div className="space-y-3">
            {themeTexts.slice(0, 3).map((t, i) => (
              <div key={i} className="flex gap-3 items-start">
                <Quote size={16} className="mt-1 shrink-0" style={{ color: accent }} />
                <p className="text-zinc-200 text-sm leading-relaxed">{t.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 1 && (
        <button data-testid={`${testPrefix}-reveal-responses`} onClick={() => setStep(2)}
          className="w-full py-3 rounded-full font-bold border border-white/15 text-white hover:bg-white/5 transition-colors">
          See all {result.reviews_count} responses
        </button>
      )}

      {/* STEP 2 — every individual response */}
      {step >= 2 && (
        <div data-testid={`${testPrefix}-responses`} className="space-y-3 animate-fade-up">
          <div className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase text-zinc-300">
            <MessageSquare size={15} style={{ color: accent }} /> All responses
          </div>
          {result.comments.map((c) => (
            <div key={c.id} className="rounded-lg border border-white/10 bg-zinc-900/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <RoasterAvatar avatar={c.reviewer_avatar} initials={initialsOf(c.reviewer_handle)} size={30} ring={accent} />
                <span className="text-sm font-bold text-zinc-200">{c.reviewer_handle || "@stranger"}</span>
              </div>
              <div className="space-y-2">
                {questions.map((q) => {
                  const v = c.answers?.[q.id];
                  if (v === undefined || v === null || v === "") return null;
                  const disp = q.type === "choice" ? (q.option_labels?.[v] || v) : v;
                  return (
                    <div key={q.id} className="text-sm">
                      <span className="text-zinc-500">{q.label}: </span>
                      <span className="text-zinc-200">{String(disp)}{q.type === "scale" ? `/${q.max}` : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
